import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Layers, Workflow, FlaskConical, ArrowUpRight } from "lucide-react";
import { fadeUp, viewport, easeOut } from "@/lib/motion";
import { TID } from "@/constants/site";
import { TabVisual } from "@/components/Visuals";

const TABS = [
  {
    id: "experiences",
    label: "AI-Powered Experiences",
    icon: Layers,
    goal: "Sector applications & platforms",
    body: "We build AI-powered applications that transform how people access information, interact with services and make decisions. By combining domain understanding with intelligent technology, we create experiences that help users navigate complexity, uncover relevant insights and make better-informed choices.",
  },
  {
    id: "workflows",
    label: "Intelligent Business Workflows",
    icon: Workflow,
    goal: "SME enablement",
    body: "We develop practical AI solutions that help small and growing businesses enhance the everyday workflows that drive their operations. By applying AI where work happens most, we help businesses unlock new capabilities and adapt to a more intelligent way of working.",
  },
  {
    id: "pipeline",
    label: "Applications in Development",
    icon: FlaskConical,
    goal: "Active exploration",
    body: "We are continuously exploring and developing AI applications designed around real-world challenges. Our focus is on creating solutions that combine meaningful user experiences, relevant intelligence and measurable value.",
  },
];

const WhatWeBuild = () => {
  const [active, setActive] = useState(TABS[0].id);
  const tab = TABS.find((t) => t.id === active);

  return (
    <section id="build" className="relative py-28 lg:py-36 bg-[#E3F1F2] border-t border-black/5">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport}>
          <span className="badge-pill">
            <span className="h-1.5 w-1.5 rounded-full bg-[#43B0CE]" />
            What We Build
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight max-w-4xl leading-[1.1]">
            From idea to <span className="text-[#43B0CE]">working application.</span>
          </h2>
          <p className="mt-6 max-w-3xl text-lg font-light leading-relaxed text-[#3C5156]">
            We build practical AI applications and digital products — designed around real business and real
            people, not around the technology itself. Every application starts the same way: not with "what can
            AI do?" but with "what decision are you trying to make, and how can we make that easier?"
          </p>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5 flex flex-col gap-3">
            {TABS.map((t) => {
              const on = t.id === active;
              return (
                <button
                  key={t.id}
                  data-testid={TID.build.tab(t.id)}
                  onClick={() => setActive(t.id)}
                  className={`text-left rounded-2xl border p-6 transition-all duration-300 ${on ? "border-[#7FD0E6]/50 bg-[#FFFFFF]" : "border-black/10 bg-transparent hover:border-black/20"}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <t.icon className={`h-5 w-5 ${on ? "text-[#43B0CE]" : "text-[#64787E]"}`} />
                      <span className={`font-display text-lg font-semibold ${on ? "text-[#0B2429]" : "text-[#3C5156]"}`}>{t.label}</span>
                    </div>
                    <ArrowUpRight className={`h-5 w-5 transition-transform ${on ? "text-[#7FD0E6] rotate-0" : "text-[#64787E] -rotate-45"}`} />
                  </div>
                  <span className="mt-2 block font-mono-ed text-[10px] tracking-[0.2em] uppercase text-[#64787E] pl-8">{t.goal}</span>
                </button>
              );
            })}
          </div>

          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={tab.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.5, ease: easeOut }}
                className="rounded-3xl overflow-hidden border border-black/10 bg-[#FFFFFF]"
              >
                <TabVisual variant={tab.id} />
                <div className="p-8">
                  <span className="font-mono-ed text-[10px] tracking-[0.2em] uppercase text-[#7FD0E6]">{tab.goal}</span>
                  <h3 className="mt-2 font-display text-2xl font-bold text-[#0B2429]">{tab.label}</h3>
                  <p className="mt-4 text-[15px] leading-relaxed text-[#3C5156]">{tab.body}</p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="mt-16 rounded-3xl border border-black/10 bg-gradient-to-br from-[#F4FAFB] to-transparent p-10 lg:p-14"
        >
          <span className="font-mono-ed text-[11px] tracking-[0.3em] uppercase text-[#43B0CE]">From Idea to Working Application</span>
          <p className="mt-5 max-w-4xl font-quote text-2xl sm:text-3xl leading-snug text-[#0B2429]">
            Good AI products don't start with the code. They start with a clear problem or gap.
          </p>
          <p className="mt-6 max-w-3xl text-[15px] leading-relaxed text-[#3C5156]">
            We work closely with every client to understand what they're actually trying to solve — then design
            and build something that fits their real workflow, not a generic template. The result isn't a demo.
            It's not a proof of concept collecting dust. It's a product built to be used — because that's the only
            kind of AI that actually closes the gap.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default WhatWeBuild;
